import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { portfolioProjects } from '../mock';

/* ------------------------------------------------------------------ */
/* FolderReveal — the folder slides in from the right *while* you      */
/* scroll (scrubbed to scroll position, not a one-shot trigger), then  */
/* the project documents rise out of it as you keep scrolling.         */
/* ------------------------------------------------------------------ */

const DOC_TONES = ['#a855f7', '#ec4899', '#0ea5e9', '#22c55e'];

export default function FolderReveal() {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end 0.35'],
  });

  // Smooth the raw scroll value so the motion feels seamless.
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 22, mass: 0.4 });

  // Phase 1 (0 → 0.45): folder slides in from the right edge.
  const folderX = useTransform(progress, [0, 0.45], ['110%', '0%']);
  const folderRot = useTransform(progress, [0, 0.45], [5, 0]);
  const folderOpacity = useTransform(progress, [0, 0.2], [0, 1]);

  // Phase 2 (0.5 → 0.95): documents rise out of the folder, fanned.
  const d0y = useTransform(progress, [0.5, 0.85], [140, 0]);
  const d1y = useTransform(progress, [0.57, 0.89], [140, 0]);
  const d2y = useTransform(progress, [0.64, 0.93], [140, 0]);
  const d3y = useTransform(progress, [0.71, 0.97], [140, 0]);
  const d0r = useTransform(progress, [0.5, 0.9], [0, -4]);
  const d1r = useTransform(progress, [0.57, 0.9], [0, -1.5]);
  const d2r = useTransform(progress, [0.64, 0.9], [0, 1.5]);
  const d3r = useTransform(progress, [0.71, 0.9], [0, 4]);
  const docMotion = [
    { y: d0y, rot: d0r },
    { y: d1y, rot: d1r },
    { y: d2y, rot: d2r },
    { y: d3y, rot: d3r },
  ];

  return (
    <section ref={ref} className="relative py-28 overflow-hidden border-t border-line bg-surface">
      <div className="absolute -top-24 right-0 w-[520px] h-[380px] bloom rounded-full blur-3xl pointer-events-none opacity-60" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* ------- left: copy ------- */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="eyebrow">02</span>
              <span className="h-px w-8 bg-gradient-to-r from-purple-500 to-pink-500" />
              <span className="eyebrow">Selected work</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-strong mb-6">
              Every project ships as a<span className="ink-grad"> complete folder.</span>
            </h2>
            <p className="text-lg text-soft max-w-md leading-relaxed mb-8">
              Strategy, build, analytics, and automation — delivered together, documented, and
              ready to hand over. Scroll to open a few recent ones.
            </p>
            <Link
              to="/work"
              className="font-mono-ui text-sm text-strong hover:text-purple-500 transition-colors inline-flex items-center gap-1.5 group"
            >
              browse all work
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* ------- right: the folder ------- */}
          <motion.div
            aria-hidden="true"
            style={
              reduce
                ? undefined
                : { x: folderX, rotate: folderRot, opacity: folderOpacity }
            }
            className="relative h-[380px] sm:h-[420px] select-none"
          >
            {/* back panel + tab */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-10 w-[92%] max-w-[460px] h-[260px]">
              <div
                className="absolute -top-9 left-0 w-[38%] h-12 rounded-t-2xl border border-b-0 border-line"
                style={{ background: 'hsl(var(--raise2))' }}
              >
                <span className="absolute left-4 top-2.5 font-mono-ui text-[10px] tracking-widest text-faint">
                  sync://projects
                </span>
              </div>
              <div
                className="absolute inset-0 rounded-2xl rounded-tl-none border border-line"
                style={{ background: 'hsl(var(--raise2))' }}
              />

              {/* documents — rise from inside the folder */}
              <div className="absolute inset-x-0 top-6 bottom-[36%] overflow-visible">
                {portfolioProjects.slice(0, 4).map((p, i) => (
                  <motion.div
                    key={p.id}
                    style={
                      reduce
                        ? { x: '-50%', zIndex: i }
                        : { x: '-50%', y: docMotion[i].y, rotate: docMotion[i].rot, zIndex: i }
                    }
                    className="absolute left-1/2 w-[76%] origin-bottom"
                    initial={false}
                  >
                    <div
                      className="rounded-xl bg-raise border border-line shadow-lg px-5 py-3.5"
                      style={{ transform: `translateY(${-i * 62}px)` }}
                    >
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <span
                          className="w-2 h-2 rounded-[3px] shrink-0"
                          style={{ background: DOC_TONES[i % DOC_TONES.length] }}
                        />
                        <span className="font-mono-ui text-[10px] tracking-widest text-faint uppercase">
                          {p.category} · {p.year}
                        </span>
                      </div>
                      <div className="font-display text-sm font-semibold text-strong truncate">
                        {p.title}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* front flap — masks the documents so they emerge from "inside" */}
              <div
                className="absolute inset-x-0 bottom-0 h-[40%] rounded-2xl border border-line z-10"
                style={{
                  background:
                    'linear-gradient(180deg, hsl(var(--raise)) 0%, hsl(var(--raise2)) 100%)',
                  boxShadow: '0 -18px 30px -22px hsl(var(--ink) / 0.35)',
                }}
              >
                <div className="absolute inset-x-6 bottom-5 flex items-center justify-between">
                  <span className="font-mono-ui text-[10px] tracking-widest text-faint">
                    04 FILES
                  </span>
                  <span className="flex items-end gap-[3px] h-3" aria-hidden="true">
                    {[60, 100, 45, 85].map((h, i) => (
                      <span
                        key={i}
                        className="syncbar w-[3px]"
                        style={{ height: `${h}%`, animationDelay: `${i * 0.14}s` }}
                      />
                    ))}
                  </span>
                </div>
              </div>

              {/* folder shadow */}
              <div
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[85%] h-8 rounded-[50%] blur-xl"
                style={{ background: 'hsl(var(--ink) / 0.18)' }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
